import React from 'react';
import '../styles/TermsOfService.css';

function TermsOfService() {
  return (
    <div className="terms-page">
      <div className="terms-container">
        <h1>TERMS OF SERVICE</h1>
        <p className="terms-subtitle">Last updated February 03, 2026</p>

        <h2 id="toc">TABLE OF CONTENTS</h2>
        <ol>
          <li><a href="#agreement">Agreement to Terms</a></li>
          <li><a href="#services">Description of Services</a></li>
          <li><a href="#accounts">User Accounts</a></li>
          <li><a href="#agents">Agent (Problem Poster) Terms</a></li>
          <li><a href="#workers">Worker (Developer) Terms</a></li>
          <li><a href="#payments">Payments, Bounties, and Payouts</a></li>
          <li><a href="#ai">AI-Powered Features</a></li>
          <li><a href="#ip">Intellectual Property</a></li>
          <li><a href="#conduct">User Conduct</a></li>
          <li><a href="#thirdparty">Third-Party Services</a></li>
          <li><a href="#disclaimers">Disclaimers</a></li>
          <li><a href="#liability">Limitation of Liability</a></li>
          <li><a href="#termination">Termination</a></li>
          <li><a href="#disputes">Dispute Resolution</a></li>
          <li><a href="#changes">Changes to These Terms</a></li>
          <li><a href="#contact">Contact Us</a></li>
        </ol>

        <h2 id="agreement">1. Agreement to Terms</h2>
        <p>
          These Terms of Service ("Terms") constitute a legally binding agreement between you and Hanhi, LLC ("we," "us," or "our") governing your access to and use of the CodeBounty platform at{' '}
          <a href="https://codebounty.hanhi.io" target="_blank" rel="noopener noreferrer">https://codebounty.hanhi.io</a>{' '}
          and all related services (collectively, the "Services").
        </p>
        <p>
          By accessing or using the Services, you agree to be bound by these Terms and our{' '}
          <a href="/privacy">Privacy Policy</a>. If you do not agree, you may not use the Services. You must be at least 18 years of age or the age of legal majority in your jurisdiction to use the Services.
        </p>

        <h2 id="services">2. Description of Services</h2>
        <p>
          CodeBounty is a bounty-based marketplace that connects problem posters ("Agents") with developers ("Workers") to solve programming challenges. The platform provides:
        </p>
        <ul>
          <li>A marketplace for posting and browsing programming tasks with monetary bounties</li>
          <li>AI-powered task generation from product requirement documents (PRDs)</li>
          <li>AI-assisted evaluation of submitted solutions</li>
          <li>Secure payment processing for bounties and payouts</li>
          <li>GitHub integration for code submissions</li>
          <li>Worker rankings, ratings, and profiles</li>
        </ul>
        <p>
          We act solely as a platform connecting Agents and Workers. We are not a party to the agreements between Agents and Workers and do not guarantee the outcome of any task or engagement.
        </p>

        <h2 id="accounts">3. User Accounts</h2>
        <p>
          You must create an account via GitHub OAuth to use the Services. You are responsible for maintaining the security of your account credentials and for all activity under your account. You agree to provide accurate information and to promptly update it as needed. We reserve the right to suspend or terminate accounts that violate these Terms.
        </p>

        <h2 id="agents">4. Agent (Problem Poster) Terms</h2>
        <p>As an Agent, you agree that:</p>
        <ul>
          <li>You will provide clear, accurate problem descriptions and PRDs.</li>
          <li>You will set a bounty amount before your problem becomes visible to Workers. Payment is required at the time of posting.</li>
          <li>You will review submitted solutions in good faith and in a timely manner.</li>
          <li>Once a solution is approved, the corresponding bounty portion will be released to the Worker. Approved bounties are non-reversible.</li>
          <li>You are solely responsible for the content and legality of the problems you post.</li>
        </ul>

        <h2 id="workers">5. Worker (Developer) Terms</h2>
        <p>As a Worker, you agree that:</p>
        <ul>
          <li>You are an independent contractor and not an employee, partner, or agent of Hanhi, LLC or any Agent.</li>
          <li>You are responsible for your own taxes, insurance, and compliance with applicable laws.</li>
          <li>You will submit original work that does not infringe any third-party intellectual property rights.</li>
          <li>Claimed tasks should be completed and submitted in a reasonable timeframe. Abandoned tasks may be automatically released.</li>
          <li>Solutions are subject to both AI-automated evaluation and human review by the Agent.</li>
          <li>Agents may reject solutions that do not meet their stated requirements. Rejection feedback will be provided where possible.</li>
        </ul>

        <h2 id="payments">6. Payments, Bounties, and Payouts</h2>
        <p>
          All payment processing is handled by Stripe. By using the payment features, you also agree to{' '}
          <a href="https://stripe.com/legal" target="_blank" rel="noopener noreferrer">Stripe's Terms of Service</a>.
        </p>
        <ul>
          <li><strong>Bounty Funding:</strong> Agents must fund bounties via Stripe before tasks become available. Problems remain in a pending state until payment is confirmed.</li>
          <li><strong>Worker Earnings:</strong> Approved bounty portions are credited to your platform wallet. Earnings become available for withdrawal once approved.</li>
          <li><strong>Payouts:</strong> Workers may request payouts via Stripe Connect. You must connect a valid bank account to receive funds. Minimum payout thresholds may apply.</li>
          <li><strong>Refunds:</strong> Bounty refunds are handled on a case-by-case basis. Bounties for approved work are not refundable.</li>
          <li><strong>Fees:</strong> We reserve the right to charge platform fees on transactions. Any applicable fees will be disclosed before you confirm a transaction.</li>
        </ul>

        <h2 id="ai">7. AI-Powered Features</h2>
        <p>The Services use artificial intelligence (powered by OpenAI GPT models) for:</p>
        <ul>
          <li>Breaking PRDs into individual development tasks</li>
          <li>Generating test cases for submitted solutions</li>
          <li>Evaluating code quality and correctness</li>
        </ul>
        <p>
          AI-generated outputs are provided as-is and may contain errors or inaccuracies. AI evaluations are advisory and do not replace the Agent's final review authority. We do not guarantee the accuracy, completeness, or reliability of any AI-generated content. By using the Services, you consent to your inputs (including PRDs, code, and solutions) being processed by our AI service providers as described in our{' '}
          <a href="/privacy">Privacy Policy</a>.
        </p>

        <h2 id="ip">8. Intellectual Property</h2>
        <ul>
          <li><strong>Platform IP:</strong> The CodeBounty platform, including its design, code, branding, and AI systems, is owned by Hanhi, LLC. You may not copy, modify, or distribute any part of the platform without our written consent.</li>
          <li><strong>Agent Content:</strong> Agents retain ownership of their problem descriptions and PRDs. By posting on the platform, Agents grant us a license to display and process this content as necessary to operate the Services.</li>
          <li><strong>Worker Submissions:</strong> Unless otherwise agreed between the Agent and Worker, approved and paid-for solutions are assigned to the Agent upon bounty approval. Workers retain the right to use general knowledge, skills, and techniques gained during their work.</li>
          <li><strong>Open Source:</strong> If a task involves contributions to open-source projects, the applicable open-source license governs that code.</li>
        </ul>

        <h2 id="conduct">9. User Conduct</h2>
        <p>You agree not to:</p>
        <ul>
          <li>Submit plagiarized, stolen, or AI-generated work without disclosure when claiming tasks as a Worker</li>
          <li>Post fraudulent, misleading, or illegal tasks as an Agent</li>
          <li>Manipulate ratings, rankings, or the evaluation system</li>
          <li>Harass, abuse, or threaten other users</li>
          <li>Attempt to circumvent the payment system or extract bounties without delivering genuine work</li>
          <li>Use the platform to distribute malware or malicious code</li>
          <li>Scrape, crawl, or use automated means to access the Services without permission</li>
          <li>Violate any applicable laws or regulations</li>
        </ul>

        <h2 id="thirdparty">10. Third-Party Services</h2>
        <p>
          The Services integrate with third-party platforms including GitHub, Stripe, OpenAI, Amazon Web Services, Supabase, and Google Analytics. Your use of these third-party services is subject to their respective terms and privacy policies. We are not responsible for the availability, accuracy, or practices of any third-party services.
        </p>

        <h2 id="disclaimers">11. Disclaimers</h2>
        <p>
          THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICES WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE. WE DO NOT GUARANTEE THE QUALITY, ACCURACY, OR OUTCOMES OF ANY TASKS, SOLUTIONS, OR AI EVALUATIONS ON THE PLATFORM.
        </p>

        <h2 id="liability">12. Limitation of Liability</h2>
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, HANHI, LLC AND ITS OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF THE SERVICES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, OR BUSINESS OPPORTUNITIES. OUR TOTAL LIABILITY FOR ANY CLAIM ARISING FROM THESE TERMS OR THE SERVICES SHALL NOT EXCEED THE AMOUNT YOU PAID TO US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.
        </p>

        <h2 id="termination">13. Termination</h2>
        <p>
          We may suspend or terminate your access to the Services at any time, with or without cause, including for violation of these Terms. Upon termination, any pending bounties for uncompleted tasks may be refunded to Agents at our discretion. Earned and approved wallet balances will remain available for payout subject to our standard payout procedures. Sections of these Terms that by their nature should survive termination (including IP, liability, and dispute resolution) will continue to apply.
        </p>

        <h2 id="disputes">14. Dispute Resolution</h2>
        <p>
          <strong>Between Users:</strong> Disputes between Agents and Workers regarding task quality, rejection, or payment should first be resolved directly between the parties. We may, but are not obligated to, mediate disputes at our discretion.
        </p>
        <p>
          <strong>With Us:</strong> Any dispute arising from these Terms or the Services shall be governed by the laws of the State of California, without regard to conflict of law principles. You agree to resolve disputes through binding arbitration in San Francisco, California, except where prohibited by law. You waive the right to participate in class action lawsuits or class-wide arbitration.
        </p>

        <h2 id="changes">15. Changes to These Terms</h2>
        <p>
          We may update these Terms from time to time. The updated version will be indicated by an updated date at the top of this page. If we make material changes, we may notify you by posting a prominent notice on the Services or by sending you a notification. Your continued use of the Services after any changes constitutes acceptance of the new Terms.
        </p>

        <h2 id="contact">16. Contact Us</h2>
        <p>If you have questions or concerns about these Terms, you may contact us at:</p>
        <p>
          Hanhi, LLC<br />
          267 South Van Ness Avenue<br />
          San Francisco, CA 94103<br />
          United States<br />
          <a href="mailto:paul@hanhi.io">paul@hanhi.io</a>
        </p>
      </div>
    </div>
  );
}

export default TermsOfService;
